import { type ConsentGovernanceVersion } from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import { type AdminConsentRecordType } from "src/api/client-api/consent/adminConsentRecord";
import { getConsentVersionStatus } from "../consentVersionStatus";

const CONSENT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
};

const CONSENT_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
};

export const formatDate = (value?: string | null, locale?: string) => {
    if (!value) {
        return "—";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat(locale, CONSENT_DATE_OPTIONS).format(date);
};

export const formatReadableDate = (value?: string | null, locale?: string) => {
    return formatDate(value, locale);
};

export const formatDateTimeParts = (value?: string | null, locale?: string): { date: string; time: string } | null => {
    if (!value) {
        return null;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return { date: value, time: "" };
    }

    return {
        date: formatDate(value, locale),
        time: new Intl.DateTimeFormat(locale, CONSENT_TIME_OPTIONS).format(date),
    };
};

export const formatReadableDateTimeParts = (
    value?: string | null,
    locale?: string,
): { date: string; time: string } | null => {
    if (!value) {
        return null;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return { date: value, time: "" };
    }

    return {
        date: formatDate(value, locale),
        time: new Intl.DateTimeFormat(locale, CONSENT_TIME_OPTIONS).format(date),
    };
};

export const formatConsentVersionBadgeLabel = (version?: string | null) => {
    const trimmedVersion = version?.trim();

    if (!trimmedVersion) {
        return "—";
    }

    return trimmedVersion.toLowerCase().startsWith("v") ? trimmedVersion : `v${trimmedVersion}`;
};

export const getConsentVersionBadgeLabel = (value: unknown) => {
    if (typeof value === "object" && value !== null) {
        const reference = value as Record<string, unknown>;

        if (typeof reference.version === "string") {
            return formatConsentVersionBadgeLabel(reference.version);
        }
    }

    return formatReference(value);
};

export const getConsentVersionTitle = (value: unknown): string | null => {
    if (typeof value !== "object" || value === null) {
        return null;
    }

    const title = (value as Record<string, unknown>).title;

    return typeof title === "string" && title.trim().length > 0 ? title.trim() : null;
};

export const escapeCsvValue = (value: string) => `"${value.replaceAll('"', '""')}"`;

export const stripHtml = (value?: string | null) => {
    if (!value) {
        return "";
    }

    return value
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};

export const downloadCsvFile = (filename: string, rows: string[][]) => {
    const content = `\uFEFF${rows.map((row) => row.map((cell) => escapeCsvValue(cell)).join(";")).join("\r\n")}`;
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
};

export const formatReference = (value: unknown) => {
    if (typeof value === "string") {
        return value.split("/").filter(Boolean).at(-1) ?? value;
    }

    if (typeof value !== "object" || value === null) {
        return "—";
    }

    const reference = value as Record<string, unknown>;
    const label = [
        reference.firstname,
        reference.lastname,
        reference.email,
        reference.name,
        reference.title,
        reference.code,
        reference.version,
    ]
        .filter((item): item is string => typeof item === "string" && item.trim().length > 0)
        .join(" · ");

    if (label.length > 0) {
        return label;
    }

    if (typeof reference.id === "number") {
        return String(reference.id);
    }

    if (typeof reference["@id"] === "string") {
        return reference["@id"].split("/").filter(Boolean).at(-1) ?? reference["@id"];
    }

    return "—";
};

export const getConsentItemName = (value: unknown) => {
    if (typeof value === "string") {
        return value.split("/").filter(Boolean).at(-1) ?? value;
    }

    if (typeof value !== "object" || value === null) {
        return "—";
    }

    const reference = value as Record<string, unknown>;

    for (const key of ["name", "title", "code"]) {
        const fieldValue = reference[key];

        if (typeof fieldValue === "string" && fieldValue.trim().length > 0) {
            return fieldValue.trim();
        }
    }

    if (typeof reference.id === "number") {
        return String(reference.id);
    }

    if (typeof reference["@id"] === "string") {
        return reference["@id"].split("/").filter(Boolean).at(-1) ?? reference["@id"];
    }

    return "—";
};

export const getConsentItemCode = (value: unknown) => {
    if (typeof value === "string") {
        return value.split("/").filter(Boolean).at(-1) ?? value;
    }

    if (typeof value !== "object" || value === null) {
        return "—";
    }

    const reference = value as Record<string, unknown>;

    if (typeof reference.code === "string" && reference.code.trim().length > 0) {
        return reference.code.trim();
    }

    return "—";
};

export const getUserDisplayName = (user: unknown): { email: string; name: string } => {
    if (typeof user !== "object" || user === null) {
        return { name: typeof user === "string" ? user : "—", email: "" };
    }

    const ref = user as Record<string, unknown>;
    const parts = [ref.firstname, ref.lastname].filter(
        (value): value is string => typeof value === "string" && value.trim().length > 0,
    );

    return {
        name: parts.length > 0 ? parts.join(" ") : "—",
        email: typeof ref.email === "string" ? ref.email : "",
    };
};

export const getConsentRecordItemType = (value: unknown): AdminConsentRecordType | null => {
    if (typeof value !== "object" || value === null) {
        return null;
    }

    const reference = value as Record<string, unknown>;

    return reference.type === "document" || reference.type === "opt_in" ? reference.type : null;
};

export const getCatalogAvailabilityDescriptor = (isActive: boolean) =>
    isActive ?
        { key: "settings.consent.item.visible" as const, fallback: "Visible" }
    :   { key: "settings.consent.item.hidden" as const, fallback: "Hidden" };

export const getConsentVersionStatusDescriptor = (version: ConsentGovernanceVersion) => {
    const status = getConsentVersionStatus(version);

    switch (status) {
        case "archived":
            return {
                labelKey: "settings.consent.version.status_archived" as const,
                labelFallback: "Archived",
                tagVariant: "tags-grey" as const,
            };
        case "published":
            return {
                labelKey: "settings.consent.version.status_published" as const,
                labelFallback: "Published",
                tagVariant: "tags-green" as const,
            };
        case "draft":
            return {
                labelKey: "settings.consent.version.status_draft" as const,
                labelFallback: "Draft",
                tagVariant: "tags-grey" as const,
            };
    }
};

export const formatConsentVersionActivePeriod = (
    version: ConsentGovernanceVersion,
    locale: string,
    currentLabel: string,
): string => {
    const status = getConsentVersionStatus(version);

    if (status === "draft") {
        return "—";
    }

    const publishedAt = formatDate(version.publishedAt, locale);

    if (status === "published") {
        return `${publishedAt} - ${currentLabel}`;
    }

    return `${publishedAt} - ${formatDate(version.archivedAt, locale)}`;
};

export const paginate = <T>(items: T[], page: number, rowsPerPage: number) =>
    items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
